import React, { useState, useCallback, useRef, useMemo } from 'react';

const AddressAutocomplete = ({ 
  name, 
  value, 
  onChange, 
  placeholder, 
  className, 
  required = false,
  label 
}) => {
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceTimer = useRef(null);

  // Common US addresses and landmarks for fallback - wrapped in useMemo
  const fallbackAddresses = useMemo(() => [
    // Major Airports
    "Orlando International Airport, Orlando, FL",
    "Orlando Sanford International Airport, Sanford, FL", 
    "Tampa International Airport, Tampa, FL",
    "Miami International Airport, Miami, FL",
    "Fort Lauderdale-Hollywood International Airport, Fort Lauderdale, FL",
    "Jacksonville International Airport, Jacksonville, FL",
    
    // Popular Orlando Areas
    "Universal Studios Orlando, FL",
    "Walt Disney World Resort, Bay Lake, FL",
    "Disney's Magic Kingdom, Bay Lake, FL",
    "Disney's Hollywood Studios, Bay Lake, FL",
    "EPCOT, Bay Lake, FL",
    "Disney's Animal Kingdom, Bay Lake, FL",
    "Universal's Islands of Adventure, Orlando, FL",
    "ICON Park, Orlando, FL",
    "International Drive, Orlando, FL",
    "Downtown Orlando, FL",
    "Winter Park, FL",
    "Celebration, FL",
    "Kissimmee, FL",
    "Lake Buena Vista, FL",
    
    // Hotels & Resorts - Orlando Area
    "The Ritz-Carlton Orlando Grande Lakes, FL",
    "Four Seasons Resort Orlando, FL",
    "Grand Floridian Resort & Spa, Bay Lake, FL",
    "Contemporary Resort, Bay Lake, FL",
    "Polynesian Village Resort, Bay Lake, FL",
    "Universal's Hard Rock Hotel, Orlando, FL",
    "Universal's Portofino Bay Hotel, Orlando, FL",
    "JW Marriott Orlando Grande Lakes, FL",
    "Waldorf Astoria Orlando, FL",
    "Omni Orlando Resort at ChampionsGate, FL",
    
    // Popular Orlando Destinations
    "Orange County Convention Center, Orlando, FL",
    "Amway Center, Orlando, FL",
    "Camping World Stadium, Orlando, FL",
    "City Beautiful Church, Orlando, FL",
    "Dr. Phillips Center for the Performing Arts, Orlando, FL",
    "Orlando Science Center, Orlando, FL",
    "Gatorland, Orlando, FL",
    "Fun Spot America Orlando, FL",
    "SeaWorld Orlando, FL",
    "LEGOLAND Florida, Winter Haven, FL",
    "Busch Gardens Tampa, Tampa, FL",
    
    // Shopping & Entertainment
    "The Mall at Millenia, Orlando, FL",
    "Orlando Premium Outlets, Orlando, FL",
    "Disney Springs, Lake Buena Vista, FL",
    "Universal CityWalk, Orlando, FL",
    "Park Avenue, Winter Park, FL",
    "Pointe Orlando, Orlando, FL",
    
    // Convention Centers & Business
    "Orange County Convention Center, Orlando, FL",
    "Orlando Executive Airport, Orlando, FL",
    "University of Central Florida, Orlando, FL",
    "Rollins College, Winter Park, FL",
    "Full Sail University, Winter Park, FL",
    
    // Miami Area (for trips to Miami)
    "South Beach, Miami Beach, FL",
    "Downtown Miami, FL",
    "Brickell Avenue, Miami, FL",
    "Port of Miami, FL",
    "Aventura Mall, Aventura, FL",
    
    // Beaches & Parks - Central Florida
    "Cocoa Beach, FL",
    "New Smyrna Beach, FL",
    "Daytona Beach, FL",
    "Lake Eola Park, Orlando, FL",
    "Bok Tower Gardens, Lake Wales, FL",
    "Blue Spring State Park, Orange City, FL"
  ], []);

  // Free Nominatim API for address suggestions
  const fetchAddressSuggestions = useCallback(async (query) => {
    if (query.length < 3) return;

    setIsLoading(true);
    try {
      // Using OpenStreetMap Nominatim API (completely free)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=8&q=${encodeURIComponent(query + ' Florida, USA')}&countrycodes=us`
      );
      
      if (response.ok) {
        const data = await response.json();
        const addresses = data.map(item => {
          // Format the address nicely
          const parts = [];
          if (item.address) {
            if (item.address.house_number && item.address.road) {
              parts.push(`${item.address.house_number} ${item.address.road}`);
            } else if (item.address.road) {
              parts.push(item.address.road);
            }
            if (item.address.city || item.address.town || item.address.village) {
              parts.push(item.address.city || item.address.town || item.address.village);
            }
            if (item.address.state) {
              parts.push(item.address.state);
            }
          }
          
          return parts.length > 0 ? parts.join(', ') : item.display_name;
        });
        
        // Combine API results with fallback addresses
        const combined = [...addresses, ...fallbackAddresses.filter(addr => 
          addr.toLowerCase().includes(query.toLowerCase())
        )];
        
        // Remove duplicates and limit results
        const unique = [...new Set(combined)];
        setFilteredSuggestions(unique.slice(0, 10));
      } else {
        // Fallback to local suggestions if API fails
        const filtered = fallbackAddresses.filter(address =>
          address.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredSuggestions(filtered.slice(0, 10));
      }
    } catch (error) {
      console.log('Using fallback addresses due to network issue');
      // Fallback to local suggestions if API fails
      const filtered = fallbackAddresses.filter(address =>
        address.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredSuggestions(filtered.slice(0, 10));
    } finally {
      setIsLoading(false);
    }
  }, [fallbackAddresses]);

  // Remove unused useEffect
  // useEffect(() => {
  //   setSuggestions(fallbackAddresses);
  // }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(e); // Call the parent's onChange

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (inputValue.length > 2) {
      setShowDropdown(true);
      // Debounce API calls to avoid too many requests
      debounceTimer.current = setTimeout(() => {
        fetchAddressSuggestions(inputValue);
      }, 300);
    } else {
      setFilteredSuggestions([]);
      setShowDropdown(false);
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Create a synthetic event to match the expected format
    const syntheticEvent = {
      target: {
        name: name,
        value: suggestion
      }
    };
    onChange(syntheticEvent);
    setFilteredSuggestions([]);
    setShowDropdown(false);
  };

  const handleBlur = () => {
    // Delay hiding dropdown to allow clicks
    setTimeout(() => {
      setShowDropdown(false);
    }, 150);
  };

  return (
    <div className="address-autocomplete-container">
      <input
        type="text"
        name={name}
        className={className}
        value={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={() => value.length > 2 && setShowDropdown(true)}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
      
      {/* Custom dropdown for better UX */}
      {showDropdown && (
        <div className="address-suggestions-dropdown">
          {isLoading && (
            <div className="address-suggestion-item loading">
              <span className="suggestion-icon">🔄</span>
              Searching addresses...
            </div>
          )}
          {!isLoading && filteredSuggestions.length > 0 && filteredSuggestions.map((suggestion, index) => (
            <div
              key={index}
              className="address-suggestion-item"
              onMouseDown={() => handleSuggestionClick(suggestion)}
            >
              <span className="suggestion-icon">📍</span>
              {suggestion}
            </div>
          ))}
          {!isLoading && filteredSuggestions.length === 0 && value.length > 2 && (
            <div className="address-suggestion-item no-results">
              <span className="suggestion-icon">🔍</span>
              No suggestions found - you can still enter any address manually
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
